import { useRef, useEffect } from "react";
import { useForm } from "react-hook-form";
import { FormCheckboxChips } from "../../shared/formElements/formCheckBoxChip/FormCheckBoxChip";
import { FormImagePreviewAndUpload } from "../../shared/formElements/FormImagePreviewAndUpload/FormImagePreviewAndUpload";
import { Shipment } from "../../../pages/order/orderDetail/shipment/Shipment";
import classes from "./orderDetailForm.module.css";

const formatDate = (ts) => {
  if (!ts) return "—";
  return new Date(ts * 1000).toLocaleString("en-IN", {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

const formatCurrency = (val) => {
  if (val == null) return "—";
  return `₹${Number(val).toLocaleString("en-IN")}`;
};

const InfoRow = ({ label, value, mono = false }) => (
  <div className={classes.infoRow}>
    <span className={classes.infoLabel}>{label}</span>
    <span className={`${classes.infoValue} ${mono ? classes.mono : ""}`}>
      {value ?? "—"}
    </span>
  </div>
);

const SectionCard = ({ icon, title, children, className = "" }) => (
  <div className={`${classes.card} ${className}`}>
    <div className={classes.cardHeader}>
      {icon}
      <span className={classes.cardTitle}>{title}</span>
    </div>
    <div className={classes.cardBody}>{children}</div>
  </div>
);

const IconOrder = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
    <line x1="16" y1="13" x2="8" y2="13"/>
    <line x1="16" y1="17" x2="8" y2="17"/>
  </svg>
);

const IconUser = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);

const IconPayment = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
    <line x1="1" y1="10" x2="23" y2="10"/>
  </svg>
);

const IconShipment = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="3" width="15" height="13"/>
    <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/>
    <circle cx="5.5" cy="18.5" r="2.5"/>
    <circle cx="18.5" cy="18.5" r="2.5"/>
  </svg>
);

const IconEdit = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

export const OrderDetailForm = ({
  initialValues,
  shipments = [],
  orderId,
  onShipmentChange,
  onSubmit,
  isSubmitting,
}) => {
  const normalizedUrl =
    !initialValues.url || initialValues.url === "None" ? "" : initialValues.url;

  const { register, watch, setValue } = useForm({
    defaultValues: { ...initialValues, url: normalizedUrl },
  });

  const approvalStatus = watch("approval_status_code");
  const url = watch("url");

  const isApprovalChanged = approvalStatus !== initialValues.approval_status_code;
  const isUrlChanged = url !== normalizedUrl;
  const canSubmit = isApprovalChanged || isUrlChanged;

  const approvalOptions = [
    { id: "1", label: approvalStatus === "1" ? "Approved" : "Approve" },
    { id: "2", label: approvalStatus === "2" ? "Rejected" : "Reject" },
  ];

  const handleFormSubmit = (e) => {
    e.preventDefault();
    onSubmit({ payment_id: initialValues.payment_id, status: approvalStatus, url });
  };

  const od = initialValues?.order_info?.order_details || {};

  const gatewayRef = useRef(null);
  useEffect(() => {
    const el = gatewayRef.current;
    if (!el) return;
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) {
      if (node.textContent.toLowerCase().includes("captured")) {
        node.parentElement.scrollIntoView({ block: "nearest" });
        break;
      }
    }
  }, [initialValues.payment_details]);

  return (
    <form className={classes.form} onSubmit={handleFormSubmit}>
      <div className={classes.layout}>

        {/* ─── ROW 1 COL 1: ORDER INFO ─── */}
        <SectionCard className={classes.orderInfoCard} icon={<IconOrder />} title="Order Info">
          <InfoRow label="Order ID"     value={initialValues.order_id} mono />
          <InfoRow label="Items"        value={initialValues.num_of_items} />
          <InfoRow label="Total amount" value={formatCurrency(initialValues.total_amount)} />
          <InfoRow label="Ordered on"   value={formatDate(initialValues.ordered_on)} />
        </SectionCard>

        {/* ─── ROW 1 COL 2: USER INFO ─── */}
        <SectionCard className={classes.userInfoCard} icon={<IconUser />} title="Customer Info">
          <InfoRow label="User ID"  value={od.user_id ?? initialValues.user_id} mono />
          <InfoRow label="Name"     value={od.name} />
          <InfoRow label="Mobile"   value={od.mobile_no} mono />
          <InfoRow label="Address"  value={od.address || od.area} />
        </SectionCard>

        {/* ─── ROW 2: PAYMENT DETAILS (full width) ─── */}
        <SectionCard className={classes.paymentCard} icon={<IconPayment />} title="Payment Details">
          <div className={classes.paymentGrid}>
            <div className={classes.paymentMeta}>
              <InfoRow label="Payment ID"        value={initialValues.payment_id} mono />
              <InfoRow label="Transaction ID"    value={initialValues.transaction_id} mono />
              <InfoRow label="RazorPay order ID" value={initialValues.rz_order_id} mono />
            </div>
            {initialValues.payment_details && (
              <div className={classes.gatewayWrapper}>
                <span className={classes.gatewayLabel}>Gateway response</span>
                {/* HTML from Razorpay — internal admin portal only */}
                <div
                  ref={gatewayRef}
                  className={classes.gatewayBlock}
                  dangerouslySetInnerHTML={{ __html: initialValues.payment_details }}
                />
              </div>
            )}
          </div>
        </SectionCard>

        {/* ─── ROW 3 COL 1: SHIPMENTS ─── */}
        {shipments.length > 0 && (
          <SectionCard className={classes.shipmentsCard} icon={<IconShipment />} title="Shipments">
            <div className={classes.shipmentsWrapper}>
              {shipments.map((shipment) => (
                <Shipment
                  key={shipment.shipment_id}
                  shipment={shipment}
                  orderId={orderId}
                />
              ))}
            </div>
          </SectionCard>
        )}

        {/* ─── ROW 3 COL 2: ACTIONS ─── */}
        <SectionCard className={classes.actionsCard} icon={<IconEdit />} title="Actions">
          <div className={classes.actionField}>
            <label className={classes.fieldLabel}>Payment screenshot</label>
            <FormImagePreviewAndUpload
              id="url"
              url={url}
              register={register}
              setValue={setValue}
            />
          </div>

          <div className={classes.actionField}>
            <label className={classes.fieldLabel}>Approval status</label>
            <FormCheckboxChips
              id="approval_status_code"
              options={approvalOptions}
              watch={watch}
              setValue={setValue}
              register={register}
            />
          </div>

          <div className={classes.formActions}>
            <button
              type="submit"
              className={classes.submitBtn}
              disabled={!canSubmit || isSubmitting}
            >
              {isSubmitting ? "Updating…" : "Update order"}
            </button>
          </div>
        </SectionCard>

      </div>
    </form>
  );
};
